/**
 * RulesPanelButtonContainer — the pipeline toolbar entry point that opens the
 * Rules & automation panel (SE-5506).
 *
 * Self-contained: it drives the panel reactive var directly, so it can be
 * dropped into any toolbar without prop-drilling an open callback. Rules are
 * per-pipeline, so it renders nothing without a board id.
 * @remarks Container component — owns the open action.
 */

import { memo, useCallback } from "react";

import { Button } from "@/components/atoms/Button";

import { useRulesPanelState } from "../../../hooks/useRulesPanelState";

/** Props for {@link RulesPanelButtonContainer}. */
export interface RulesPanelButtonContainerProps {
  /** The pipeline whose rules to manage. Omit/empty to render nothing. */
  readonly kanbanBoardId?: string;
}

/**
 * Toolbar button that opens the Rules & automation panel.
 * @param props - {@link RulesPanelButtonContainerProps}
 * @param props.kanbanBoardId - The pipeline whose rules to manage
 * @returns The button, or null when there is no board.
 */
const RulesPanelButtonContainer = ({
  kanbanBoardId,
}: RulesPanelButtonContainerProps) => {
  const { open } = useRulesPanelState();

  const handlePress = useCallback(() => {
    if (kanbanBoardId) open(kanbanBoardId);
  }, [open, kanbanBoardId]);

  if (!kanbanBoardId) return null;

  return (
    <Button
      label="Rules"
      variant="outline"
      tone="secondary"
      size="sm"
      labelTone="primary"
      /* SE-6865: toolbar radius + label weight, set at the call site on
         purpose — see the anchor note in ReviewQueueButtonContainer for the two
         atom-level defects this deliberately does NOT fix. */
      shape="rounded"
      labelVariant="body-strong"
      /* SE-6554: grey toolbar border — see the Filters waiver in TopPanelView. */
      className="web:hover:bg-surface-subtle border-outline-strong"
      accessibilityLabel="Open rules and automation"
      testID="rules-panel-open"
      onPress={handlePress}
    />
  );
};

RulesPanelButtonContainer.displayName = "RulesPanelButtonContainer";

export default memo(RulesPanelButtonContainer);
